import React, { useState } from 'react'
import { Box, InputBase, styled, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
    ApplicationsList,
    FunctionsList,
    ItemDetails,
    PeopleList,
    SuggestedRoles,
    TableGrid,
    TCodesList,
} from 'components'
import { Pagination, ThresholdSearchInput } from 'packages/eid-ui'
import { Skeleton } from '@mui/material'
import {
    useBusinessRoleApplicationRolesGranted,
    useBusinessRoleApplications,
    useBusinessRoleApprovers,
    useBusinessRoleBr,
    useBusinessRoleGlobalSenstiveFunctions,
    useBusinessRoleLocalSensitiveFunctions,
    useBusinessRoleManagementRolesGranted,
    useBusinessRoleOwners,
    useBusinessRoleSuggestedApplicationRoles,
    useBusinessRoleSuggestedManagementRoles,
    useTargetPerson,
    useResponsibleParty,
} from 'hooks'
import { CheckedIcon } from 'packages/eid-icons'
import { isNilOrEmpty, useIsSmallScreen } from 'packages/core'
import useSubcomponents from 'useSubcomponents'
import cartHelpers from 'containers/Cart/cartHelpers'
import { CommonAttributes } from '../../CommonAttributes'
import { ResourceTypesNamespace, useRegistry } from 'core'
import { RenewRevoke } from 'components/RenewRevoke'
import { useHistory } from 'react-router'
import { PersonDetails } from 'components/ApplicationBanner/PersonDetails'

const hasAccessToOwnerControl =
    'ITShop-BusinessRole-ManageAccess-Details-Owners-Control'
const hasAccessToResponsiblePartyControl =
    'ITShop-BusinessRoles-Details-ResponsibleParty-Control'

const PaddedDiv = styled(Box)({
    padding: '0 31px',
})

const overflowStyleProps = {
    maxHeight: '220px',
    overflow: 'auto',
}

const OverviewSection = ({ item }) => {
    const { t } = useTranslation()

    const businessRole = item.resource

    const [targetPerson] = useTargetPerson()

    const { data: businessRequestItem, isInitialLoading } = useBusinessRoleBr(
        targetPerson.id,
        item.assignment.resourceAssignment.resourceAssignmentId,
        item.assignment.resourceAssignment.resourceTypeId,
    )

    const attributeOrientation = useIsSmallScreen() ? 'vertical' : 'horizontal'

    return (
        <ItemDetails.AttributesContainer>
            <PaddedDiv>
                <ItemDetails.Attribute
                    label={t('Common_TechnicalName')}
                    value={businessRole.name}
                    orientation={attributeOrientation}
                    valueProps={overflowStyleProps}
                />
                {businessRole.businessDomain_FriendlyName && (
                    <ItemDetails.Attribute
                        label={t('BusinessRoles_Domain')}
                        value={businessRole.businessDomain_FriendlyName}
                        orientation={attributeOrientation}
                        valueProps={overflowStyleProps}
                    />
                )}
                <ItemDetails.Attribute
                    label={t('BusinessRoles_DifferentiationValue')}
                    value={item?.resource?.differentiationValue}
                    orientation={attributeOrientation}
                    valueProps={overflowStyleProps}
                />
                <ItemDetails.Attribute
                    label={t('BusinessRoles_HighLevelClassification')}
                    value={businessRole.highLevelClassification}
                    orientation={attributeOrientation}
                    valueProps={overflowStyleProps}
                />
                <ItemDetails.Attribute
                    label={t('Common_Description')}
                    value={businessRole.description}
                    orientation={attributeOrientation}
                    valueProps={overflowStyleProps}
                />
                {isInitialLoading && <Skeleton height={32} />}
                <CommonAttributes
                    item={item}
                    businessRequestItem={businessRequestItem}
                />
            </PaddedDiv>
        </ItemDetails.AttributesContainer>
    )
}

const AdditionalInformationSection = ({ item }) => {
    const take = 10
    const { t } = useTranslation()
    const attributeOrientation = useIsSmallScreen() ? 'vertical' : 'horizontal'
    const businessRole = item.resource
    const { hasAccessToControl } = useSubcomponents()
    const isSmallScreen = useIsSmallScreen()

    const {
        data: responsibleParty,
        isInitialLoading: responsiblePartyLoading,
    } = useResponsibleParty(
        businessRole.id,
        Boolean(hasAccessToControl(hasAccessToResponsiblePartyControl)),
    )

    const [page, setPage] = React.useState(1)
    const [perPageItems, setPerPageItems] = useState(take)
    const [searchKey, setSearchKey] = useState('')

    const handlePageChange = (_, value) => {
        setPage(value)
    }
    const handlePageSelection = (value) => {
        setPage(value)
    }
    const handleItemsPerPageChange = (value) => {
        setPage(1)
        setPerPageItems(value)
    }

    const handleSearchChange = (value) => {
        setPage(1)
        setSearchKey(value)
    }

    const { data: owners, isInitialLoading: ownersLoading } =
        useBusinessRoleOwners(businessRole.id)

    const { data } = useBusinessRoleGlobalSenstiveFunctions(
        businessRole.id,
        (page - 1) * perPageItems,
        perPageItems,
        searchKey && encodeURIComponent(searchKey),
    )

    const globalSensitiveFunctions = data ? data.data : undefined

    const globalFunctionsLoading = !Boolean(data)

    const numberOfPages = data ? Math.ceil(data.totalCount / perPageItems) : 0

    return (
        <>
            {ownersLoading || responsiblePartyLoading ? (
                <ItemDetails.AttributesContainer>
                    <PaddedDiv>
                        <ItemDetails.AttributesLoader />
                    </PaddedDiv>
                </ItemDetails.AttributesContainer>
            ) : (
                <ItemDetails.AttributesContainer bottomPadding="0">
                    {hasAccessToControl(hasAccessToOwnerControl) && (
                        <Box
                            padding="0 31px 8.5px 31px"
                            data-protectedsubcomponent={hasAccessToOwnerControl}
                        >
                            <ItemDetails.Attribute
                                label={t('BusinessRoles_Owners')}
                                value={<PeopleList list={owners} />}
                                orientation={attributeOrientation}
                            />
                        </Box>
                    )}
                    {hasAccessToControl(hasAccessToResponsiblePartyControl) && (
                        <Box
                            padding="0 31px 8.5px 31px"
                            data-protectedsubcomponent={
                                hasAccessToResponsiblePartyControl
                            }
                        >
                            <ItemDetails.Attribute
                                label={t('Applications_Owner')}
                                value={
                                    responsibleParty ? (
                                        <PersonDetails
                                            size="medium"
                                            person={{
                                                imageThumbUrl:
                                                    responsibleParty.imageThumbUrl,
                                                friendlyName:
                                                    responsibleParty.friendlyName,
                                                email: responsibleParty.email,
                                                telephone:
                                                    responsibleParty.telephone,
                                            }}
                                        />
                                    ) : (
                                        <>-</>
                                    )
                                }
                                orientation={attributeOrientation}
                            />
                        </Box>
                    )}

                    {!isNilOrEmpty(searchKey) ||
                    (globalSensitiveFunctions &&
                        globalSensitiveFunctions.length > 0) ? (
                        <>
                            <PaddedDiv
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '16px 31px',
                                }}
                            >
                                <Box width="50%">
                                    <Typography
                                        style={{
                                            fontSize: '12px',
                                            lineHeight: 1.33,
                                            color: '#9b9b9b',
                                            wordBreak: 'break-word',
                                            textTransform: 'uppercase',
                                            minWidth: '80px',
                                        }}
                                    >
                                        {t('Common_GlobalFunctionalAccess')}
                                    </Typography>
                                </Box>

                                <Box
                                    width={!isSmallScreen ? '308px' : '50%'}
                                    minWidth="130px"
                                >
                                    <ThresholdSearchInput
                                        placeholder={'Common_Search'}
                                        handleSearch={handleSearchChange}
                                    />
                                </Box>
                            </PaddedDiv>
                            <Box overflow="auto">
                                <FunctionsList
                                    data={globalSensitiveFunctions}
                                    loading={globalFunctionsLoading}
                                />

                                <Box
                                    padding={
                                        !isSmallScreen ? '20px 32px' : '16px'
                                    }
                                    display="flex"
                                    alignItems="center"
                                    justifyContent={
                                        !isSmallScreen ? 'flex-start' : 'center'
                                    }
                                >
                                    <Pagination
                                        count={numberOfPages}
                                        size={
                                            !isSmallScreen ? 'large' : 'small'
                                        }
                                        page={page}
                                        onChange={handlePageChange}
                                        totalCount={data?.totalCount}
                                        onPageSelection={handlePageSelection}
                                        itemsPerPage={perPageItems}
                                        onItemsPerPageChange={
                                            handleItemsPerPageChange
                                        }
                                        showPageSelection={true}
                                        showItemsPerPageSelection
                                        showCountStat
                                    />
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <Box marginY="20px" />
                    )}
                </ItemDetails.AttributesContainer>
            )}
        </>
    )
}

const ApplicationRolesGranted = ({ businessRole, differentiationValue }) => {
    const isSmallScreen = useIsSmallScreen()
    const take = 10

    const { t } = useTranslation()

    const [page, setPage] = React.useState(1)
    const [perPageItems, setPerPageItems] = useState(take)
    const [searchKey, setSearchKey] = useState('')

    const handlePageChange = (_, value) => {
        setPage(value)
    }
    const handlePageSelection = (value) => {
        setPage(value)
    }
    const handleItemsPerPageChange = (value) => {
        setPage(1)
        setPerPageItems(value)
    }

    const handleSearchChange = (value) => {
        setPage(1)
        setSearchKey(value)
    }

    const { data } = useBusinessRoleApplicationRolesGranted(
        businessRole.id,
        differentiationValue?.id,
        (page - 1) * perPageItems,
        perPageItems,
        searchKey && encodeURIComponent(searchKey),
    )
    const list = data ? data.data : undefined

    const isInitialLoading = !Boolean(data)

    const numberOfPages = data ? Math.ceil(data.totalCount / perPageItems) : 0

    const attributes = [
        {
            label: '',
            resolve: () => <CheckedIcon />,
        },
        {
            label: t('Common_FriendlyName'),
            resolve: (item) => <> {item.friendlyName}</>,
        },
        {
            label: t('Common_Description'),
            resolve: (item) => <> {item.description}</>,
        },
        {
            name: 'resourceSystemFriendlyName',
            label: t('BusinessRoles_ResourceSystem'),
        },
    ]

    return (
        <>
            <Box
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                padding="16px 32px 16px 32px"
                style={{ backgroundColor: '#ebebed' }}
            >
                <Box width={!isSmallScreen ? '308px' : '200px'}>
                    <ThresholdSearchInput
                        placeholder={'Common_Search'}
                        handleSearch={handleSearchChange}
                    />
                </Box>
            </Box>
            <TableGrid
                loading={isInitialLoading}
                data={list}
                attributes={attributes}
            />

            {!isInitialLoading && (
                <Box
                    padding={!isSmallScreen ? '20px 32px' : '16px'}
                    display="flex"
                    alignItems="center"
                    justifyContent={!isSmallScreen ? 'flex-start' : 'center'}
                >
                    <Pagination
                        count={numberOfPages}
                        size={!isSmallScreen ? 'large' : 'small'}
                        page={page}
                        onChange={handlePageChange}
                        totalCount={data?.totalCount}
                        onPageSelection={handlePageSelection}
                        itemsPerPage={perPageItems}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        showPageSelection={true}
                        showItemsPerPageSelection
                        showCountStat
                    />
                </Box>
            )}
        </>
    )
}

const ManagementRolesGranted = ({ businessRole, differentiationValue }) => {
    const isSmallScreen = useIsSmallScreen()
    const take = 10

    const { t } = useTranslation()

    const [page, setPage] = React.useState(1)
    const [perPageItems, setPerPageItems] = useState(take)
    const [searchKey, setSearchKey] = useState('')

    const handlePageChange = (_, value) => {
        setPage(value)
    }
    const handlePageSelection = (value) => {
        setPage(value)
    }
    const handleItemsPerPageChange = (value) => {
        setPage(1)
        setPerPageItems(value)
    }

    const handleSearchChange = (value) => {
        setPage(1)
        setSearchKey(value)
    }

    const { data } = useBusinessRoleManagementRolesGranted(
        businessRole.id,
        differentiationValue?.id,
        (page - 1) * perPageItems,
        perPageItems,
        searchKey && encodeURIComponent(searchKey),
    )
    const list = data ? data.data : undefined

    const isInitialLoading = !Boolean(data)

    const numberOfPages = data ? Math.ceil(data.totalCount / perPageItems) : 0

    const attributes = [
        {
            label: '',
            resolve: () => <CheckedIcon />,
        },
        {
            label: t('Common_FriendlyName'),
            resolve: (item) => <> {item.friendlyName}</>,
        },
        {
            name: 'resourceSystemFriendlyName',
            label: t('BusinessRoles_ResourceSystem'),
        },
    ]

    return (
        <>
            <Box
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                padding="16px 32px 16px 32px"
                style={{ backgroundColor: '#ebebed' }}
            >
                <Box width={!isSmallScreen ? '308px' : '200px'}>
                    <ThresholdSearchInput
                        handleSearch={handleSearchChange}
                        placeholder={'Common_Search'}
                    />
                </Box>
            </Box>
            <TableGrid
                loading={isInitialLoading}
                data={list}
                attributes={attributes}
            />

            {!isInitialLoading && (
                <Box
                    padding={!isSmallScreen ? '20px 32px' : '16px'}
                    display="flex"
                    alignItems="center"
                    justifyContent={!isSmallScreen ? 'flex-start' : 'center'}
                >
                    <Pagination
                        count={numberOfPages}
                        size={!isSmallScreen ? 'large' : 'small'}
                        page={page}
                        onChange={handlePageChange}
                        totalCount={data?.totalCount}
                        onPageSelection={handlePageSelection}
                        itemsPerPage={perPageItems}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        showPageSelection={true}
                        showItemsPerPageSelection
                        showCountStat
                    />
                </Box>
            )}
        </>
    )
}

const LocalSensitiveFunctions = ({ businessRole, differentiationValue }) => {
    const take = 10

    const { t } = useTranslation()

    const isSmallScreen = useIsSmallScreen()

    const [page, setPage] = React.useState(1)
    const [perPageItems, setPerPageItems] = useState(take)
    const [searchKey, setSearchKey] = useState('')

    const handlePageChange = (_, value) => {
        setPage(value)
    }
    const handlePageSelection = (value) => {
        setPage(value)
    }
    const handleItemsPerPageChange = (value) => {
        setPage(1)
        setPerPageItems(value)
    }

    const handleSearchChange = (value) => {
        setPage(1)
        setSearchKey(value)
    }

    const { data } = useBusinessRoleLocalSensitiveFunctions(
        businessRole.id,
        differentiationValue?.id,
        (page - 1) * perPageItems,
        perPageItems,
        searchKey && encodeURIComponent(searchKey),
    )

    const list = data ? data.data : undefined
    const isInitialLoading = !Boolean(data)

    const numberOfPages = data ? Math.ceil(data.totalCount / perPageItems) : 0

    return (
        <>
            <Box
                width="100%"
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                padding="16px 32px 16px 32px"
                style={{ backgroundColor: '#ebebed' }}
            >
                <Box width={!isSmallScreen ? '308px' : '200px'}>
                    <ThresholdSearchInput
                        placeholder={'Common_Search'}
                        handleSearch={handleSearchChange}
                    />
                </Box>
            </Box>

            <FunctionsList loading={isInitialLoading} data={list} />
            {!isInitialLoading && (
                <Box
                    padding={!isSmallScreen ? '20px 32px' : '16px'}
                    display="flex"
                    alignItems="center"
                    justifyContent={!isSmallScreen ? 'flex-start' : 'center'}
                >
                    <Pagination
                        count={numberOfPages}
                        size={!isSmallScreen ? 'large' : 'small'}
                        page={page}
                        onChange={handlePageChange}
                        totalCount={data?.totalCount}
                        onPageSelection={handlePageSelection}
                        itemsPerPage={perPageItems}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        showPageSelection={true}
                        showItemsPerPageSelection
                        showCountStat
                    />
                </Box>
            )}
        </>
    )
}

const Approvers = ({ businessRole, differentiationValue }) => {
    const { data: approvers, isInitialLoading } = useBusinessRoleApprovers(
        businessRole.id,
        differentiationValue.id,
    )
    if (isInitialLoading)
        return (
            <Box padding="16px 32px 16px 32px" borderBottom="solid 1px #d8dadd">
                <Box margin="4px 8px">
                    <Skeleton
                        animation="wave"
                        variant="rectangular"
                        height={20}
                    />
                </Box>
                <Box margin="4px 8px">
                    <Skeleton
                        animation="wave"
                        variant="rectangular"
                        height={20}
                    />
                </Box>
            </Box>
        )
    return (
        <Box
            width="100%"
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            padding="16px 32px 16px 32px"
            borderBottom="solid 1px #d8dadd"
        >
            <PeopleList list={approvers} expandedStyles={false} />
        </Box>
    )
}

const BusinessRoleDetails = ({ item, toggleDrawer }) => {
    const { t } = useTranslation()

    const registry = useRegistry()

    const history = useHistory()
    const isManageAccess =
        history.location.pathname.indexOf('/manageAccess') >= 0

    const businessRoleType = registry.get(
        ResourceTypesNamespace,
        'BusinessRoles',
    )

    const {
        hasAccessToTCodesGrid,
        hasAccessToLocalSensitiveFunctionsGrid,
        canSeeManagementRolesGranted,
        canSeeApplicationRolesGranted,
        canSeeApplications,
        canSeeManagementRolesSuggestedGrid,
    } = useSubcomponents()

    const businessRole = item.resource

    const [targetPerson] = useTargetPerson()

    const [activeTab, setActiveTab] = useState('overview')
    const [subActiveTab, setSubActiveTab] = useState('applicationRolesGranted')

    const differentiationValue = {
        id: item.assignment.resourceAssignment.locationId,
        friendlyName: item.assignment.resourceAssignment.locationFriendlyName,
    }

    const preRevoke = () => {
        const itemToRevoke = cartHelpers.businessRoleToCartItem({
            targetPerson,
            assignmentType: 'Remove',
            businessRole: item.resource,
            differentiationValue,
            assignmentStatus: item.assignment,
        })
        if (!itemToRevoke) return undefined

        return itemToRevoke
    }

    return (
        <>
            <Box>
                <ItemDetails.Tabs
                    variant="standard"
                    value={activeTab}
                    onChange={(_, value) => setActiveTab(value)}
                >
                    <ItemDetails.Tabs.Tab
                        value="overview"
                        label={t('Common_Overview')}
                    />
                    <ItemDetails.Tabs.Tab
                        value="additionalInformation"
                        label={t('Common_MoreInformation')}
                    />
                </ItemDetails.Tabs>
            </Box>
            {activeTab === 'overview' && <OverviewSection item={item} />}
            {activeTab === 'additionalInformation' && (
                <AdditionalInformationSection item={item} />
            )}
            <Box paddingRight="12px">
                <ItemDetails.Tabs
                    variant={'scrollable'}
                    scrollButtons={'on'}
                    value={subActiveTab}
                    onChange={(_, value) => setSubActiveTab(value)}
                >
                    {canSeeApplicationRolesGranted && (
                        <ItemDetails.Tabs.Tab
                            value="applicationRolesGranted"
                            label={t('BusinessRoles_ApplicationRolesGranted')}
                            data-protectedsubcomponent={
                                canSeeApplicationRolesGranted
                            }
                        />
                    )}

                    {canSeeManagementRolesGranted && (
                        <ItemDetails.Tabs.Tab
                            value="managementRolesGranted"
                            label={t('Common_ManagementRolesGranted')}
                            data-protectedsubcomponent={
                                canSeeManagementRolesGranted
                            }
                        />
                    )}

                    {hasAccessToLocalSensitiveFunctionsGrid && (
                        <ItemDetails.Tabs.Tab
                            value="localSensitiveFunctions"
                            label={t('Common_LocalFunctionalAccess')}
                            data-protectedsubcomponent={
                                hasAccessToLocalSensitiveFunctionsGrid
                            }
                        />
                    )}
                    <ItemDetails.Tabs.Tab
                        value="approvers"
                        label={t('Common_Approvers')}
                        disabled={isNilOrEmpty(differentiationValue)}
                    />
                    {hasAccessToTCodesGrid && (
                        <ItemDetails.Tabs.Tab
                            value="tCodes"
                            label={t('Common_TCodes')}
                            disabled={isNilOrEmpty(differentiationValue)}
                            data-protectedsubcomponent={hasAccessToTCodesGrid}
                        />
                    )}

                    {canSeeApplications && (
                        <ItemDetails.Tabs.Tab
                            value="applications"
                            label={t('Common_Applications')}
                            data-protectedsubcomponent={canSeeApplications}
                        />
                    )}
                </ItemDetails.Tabs>
            </Box>
            {subActiveTab === 'applicationRolesGranted' && (
                <ApplicationRolesGranted
                    businessRole={businessRole}
                    differentiationValue={differentiationValue}
                />
            )}

            {subActiveTab === 'managementRolesGranted' && (
                <ManagementRolesGranted
                    businessRole={businessRole}
                    differentiationValue={differentiationValue}
                />
            )}

            {subActiveTab === 'localSensitiveFunctions' && (
                <LocalSensitiveFunctions
                    businessRole={businessRole}
                    differentiationValue={differentiationValue}
                />
            )}
            {subActiveTab === 'approvers' && (
                <Approvers
                    businessRole={businessRole}
                    differentiationValue={differentiationValue}
                />
            )}
            {subActiveTab === 'tCodes' && (
                <TCodesList
                    resourceId={item.resource.id}
                    differentiationValueId={differentiationValue.id}
                />
            )}
            {subActiveTab === 'applications' && (
                <ApplicationsList
                    resource={businessRole}
                    useDataHook={useBusinessRoleApplications}
                    locationId={differentiationValue.id}
                />
            )}

            {isManageAccess && (
                <Box
                    display="flex"
                    padding="0 31px"
                    margin="16px 0px"
                    gap="10px"
                >
                    <RenewRevoke
                        resourceType={businessRoleType}
                        resource={businessRole}
                        secondary={differentiationValue}
                        assignment={item.assignment}
                        postRenew={toggleDrawer}
                        preRevoke={preRevoke}
                        postRevoke={toggleDrawer}
                        item={item}
                    />
                </Box>
            )}

            <Box
                overflow="auto"
                width="100%"
                borderTop=" solid 1px #d8dadd"
                id="SuggestedApplicationRoles"
            >
                <SuggestedRoles
                    title={t('BusinessRoles_SuggestedApplicationRoles')}
                    toggleDrawer={toggleDrawer}
                    resource={businessRole}
                    differentiationValue={differentiationValue}
                    targetPerson={targetPerson}
                    useDataHook={useBusinessRoleSuggestedApplicationRoles}
                />
            </Box>
            {canSeeManagementRolesSuggestedGrid && (
                <Box
                    data-protectedsubcomponent={
                        canSeeManagementRolesSuggestedGrid
                    }
                    overflow="auto"
                    width="100%"
                    id="SuggestedManagementRoles"
                >
                    <SuggestedRoles
                        title={t('Common_SuggestedManagementRoles')}
                        toggleDrawer={toggleDrawer}
                        resource={businessRole}
                        differentiationValue={differentiationValue}
                        targetPerson={targetPerson}
                        useDataHook={useBusinessRoleSuggestedManagementRoles}
                    />
                </Box>
            )}
        </>
    )
}

export default BusinessRoleDetails
